import QRCode from 'qrcode';

// Simplified 2FA service without external dependencies
export interface TwoFactorAuthData {
  secret: string;
  qrCodeUrl: string;
  backupCodes: string[];
}

export class TwoFactorAuthService {
  private static readonly ISSUER = 'RAWPA Admin';
  private static readonly ACCOUNT_NAME = 'admin@rawpa.com';
  private static readonly SECRET_LENGTH = 32;

  /**
   * Generate a browser-compatible secret for TOTP
   */
  private static generateSecret(length: number = 32): string {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ234567'; // Base32 alphabet
    let result = '';
    const randomValues = new Uint8Array(length);
    
    // Use browser's crypto.getRandomValues if available
    if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
      crypto.getRandomValues(randomValues);
    } else {
      // Fallback to Math.random (less secure but works)
      for (let i = 0; i < length; i++) {
        randomValues[i] = Math.floor(Math.random() * 256);
      }
    }
    
    for (let i = 0; i < length; i++) {
      result += chars[randomValues[i] % chars.length];
    }
    
    return result;
  }

  /**
   * Base32 decode function (browser-compatible)
   */
  private static base32Decode(encoded: string): Uint8Array {
    const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ234567';
    const padding = '=';
    
    // Remove padding and convert to uppercase
    encoded = encoded.replace(new RegExp(padding, 'g'), '').toUpperCase();
    
    const bits = [];
    for (let i = 0; i < encoded.length; i++) {
      const char = encoded[i];
      const index = alphabet.indexOf(char);
      if (index === -1) continue;
      
      // Convert to 5-bit binary
      const binary = index.toString(2).padStart(5, '0');
      bits.push(...binary.split(''));
    }
    
    // Convert bits to bytes
    const bytes = [];
    for (let i = 0; i < bits.length; i += 8) {
      const byteBits = bits.slice(i, i + 8);
      if (byteBits.length === 8) {
        const byte = parseInt(byteBits.join(''), 2);
        bytes.push(byte);
      }
    }
    
    return new Uint8Array(bytes);
  }

  /**
   * HMAC-SHA1 function (browser-compatible)
   */
  private static async hmacSha1(key: Uint8Array, message: Uint8Array): Promise<Uint8Array> {
    const cryptoKey = await crypto.subtle.importKey(
      'raw',
      key,
      { name: 'HMAC', hash: 'SHA-1' },
      false,
      ['sign']
    );
    
    const signature = await crypto.subtle.sign('HMAC', cryptoKey, message);
    return new Uint8Array(signature);
  }

  /**
   * Generate TOTP token
   */
  private static async generateTOTP(secret: string, time: number = Date.now()): Promise<string> {
    try {
      // Decode the secret
      const key = this.base32Decode(secret);
      
      // Calculate time step (30 seconds)
      const timeStep = Math.floor(time / 30000);
      
      // Convert time step to 8-byte buffer (big-endian)
      const timeBuffer = new ArrayBuffer(8);
      const timeView = new DataView(timeBuffer);
      timeView.setBigUint64(0, BigInt(timeStep), false);
      
      // Generate HMAC-SHA1
      const hmac = await this.hmacSha1(key, new Uint8Array(timeBuffer));
      
      // Generate 4-byte code
      const offset = hmac[hmac.length - 1] & 0xf;
      const code = ((hmac[offset] & 0x7f) << 24) |
                   ((hmac[offset + 1] & 0xff) << 16) |
                   ((hmac[offset + 2] & 0xff) << 8) |
                   (hmac[offset + 3] & 0xff);
      
      // Convert to 6-digit string
      return (code % 1000000).toString().padStart(6, '0');
    } catch (error) {
      console.error('Error generating TOTP:', error);
      return '';
    }
  }

  /**
   * Generate a new 2FA secret and QR code
   */
  static async generateTwoFactorAuth(): Promise<TwoFactorAuthData> {
    try {
      console.log('Starting 2FA generation...');
      
      // Generate a new secret using our browser-compatible method
      const secret = this.generateSecret(this.SECRET_LENGTH);
      console.log('Generated secret:', secret.substring(0, 10) + '...');
      
      // Generate backup codes
      const backupCodes = this.generateBackupCodes();
      console.log('Generated backup codes:', backupCodes.length);
      
      // Create the otpauth URL for QR code
      const otpauthUrl = `otpauth://totp/${encodeURIComponent(this.ISSUER)}:${encodeURIComponent(this.ACCOUNT_NAME)}?secret=${secret}&period=30&digits=6&algorithm=SHA1&issuer=${encodeURIComponent(this.ISSUER)}`;
      console.log('Generated otpauth URL:', otpauthUrl);
      
      // Generate QR code as data URL
      const qrCodeUrl = await this.generateQRCode(otpauthUrl);
      console.log('Generated QR code URL:', qrCodeUrl.substring(0, 50) + '...');
      
      return {
        secret,
        qrCodeUrl,
        backupCodes
      };
    } catch (error) {
      console.error('Error in generateTwoFactorAuth:', error);
      throw new Error(`Failed to generate 2FA setup: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Verify a TOTP token
   */
  static async verifyToken(token: string, secret: string): Promise<boolean> {
    try {
      console.log('Verifying token:', token, 'with secret:', secret.substring(0, 10) + '...');
      
      const currentTime = Date.now();
      
      // Check current time step and adjacent steps (window of 1)
      for (let i = -1; i <= 1; i++) {
        const checkTime = currentTime + (i * 30000); // 30 seconds per step
        const generatedToken = await this.generateTOTP(secret, checkTime);
        
        if (generatedToken === token) {
          console.log('Verification successful with time offset:', i);
          return true;
        }
      }
      
      console.log('Verification failed - no matching token found');
      return false;
    } catch (error) {
      console.error('Error verifying 2FA token:', error);
      return false;
    }
  }

  /**
   * Verify a backup code
   */
  static verifyBackupCode(code: string, backupCodes: string[]): boolean {
    const normalizedCode = code.replace(/\s/g, '').toUpperCase();
    return backupCodes.includes(normalizedCode);
  }

  /**
   * Generate backup codes for account recovery
   */
  static generateBackupCodes(): string[] {
    const codes: string[] = [];
    for (let i = 0; i < 8; i++) {
      // Generate 8-digit codes with format: XXXX-XXXX
      const code = Math.random().toString(36).substring(2, 6).toUpperCase() + 
                   '-' + 
                   Math.random().toString(36).substring(2, 6).toUpperCase();
      codes.push(code);
    }
    return codes;
  }

  /**
   * Get the current TOTP token for display purposes
   */
  static async getCurrentToken(secret: string): Promise<string> {
    try {
      const token = await this.generateTOTP(secret);
      console.log('Generated current token:', token, 'for secret:', secret.substring(0, 10) + '...');
      return token;
    } catch (error) {
      console.error('Error generating current token:', error);
      return '';
    }
  }

  /**
   * Format backup codes for display
   */
  static formatBackupCodes(codes: string[]): string {
    return codes.join('\n');
  }

  /**
   * Generate QR code as data URL using qrcode library
   */
  private static async generateQRCode(url: string): Promise<string> {
    try {
      console.log('Generating QR code for URL:', url);
      
      const qrCodeUrl = await QRCode.toDataURL(url, {
        width: 200,
        margin: 2,
        color: {
          dark: '#000000',
          light: '#FFFFFF'
        },
        errorCorrectionLevel: 'M'
      });
      
      console.log('QR code generated successfully');
      return qrCodeUrl;
    } catch (error) {
      console.error('Error generating QR code:', error);
      // Fallback: return a placeholder
      return 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgZmlsbD0iI2Y5ZjlmOSIvPjx0ZXh0IHg9IjEwMCIgeT0iMTAwIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMTQiIGZpbGw9IiM5OTkiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIj5FcnJvciBnZW5lcmF0aW5nIFE8L3RleHQ+PC9zdmc+';
    }
  }
}

// Store 2FA data in localStorage (in production, this should be stored securely)
export const TwoFactorAuthStorage = {
  getSecret(): string | null {
    return localStorage.getItem('rawpa_2fa_secret');
  },

  setSecret(secret: string): void {
    localStorage.setItem('rawpa_2fa_secret', secret);
  },

  getBackupCodes(): string[] | null {
    const codes = localStorage.getItem('rawpa_2fa_backup_codes');
    return codes ? JSON.parse(codes) : null;
  },

  setBackupCodes(codes: string[]): void {
    localStorage.setItem('rawpa_2fa_backup_codes', JSON.stringify(codes));
  },

  isSetup(): boolean {
    return !!this.getSecret();
  },

  clear(): void {
    localStorage.removeItem('rawpa_2fa_secret');
    localStorage.removeItem('rawpa_2fa_backup_codes');
  }
}; 